import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { User, Calendar, Phone, Mail, Upload, Heart, CheckCircle, XCircle, Download } from 'lucide-react';
import toast from 'react-hot-toast';
import { Toast } from '../../components/Toast';
import { RegistrationCounter } from '../../components/RegistrationCounter';

const material =
{
  title: "POP MY CHERRY NOW",
  format: "PDF",
  size: "2.5 MB",
  description: "Download the book now!",
  image: "https://files.selar.co/product-images/2023/products/titoluwa-sam-oladapo1/pop-my-cherry-now-selar.co-650d1e3b5d3ef.jpeg",
  url: "https://selar.com/15293h"
}


export default function PopMyCherry() {
  const [form, setForm] = useState({
    full_name: "",
    age: "",
    marriage_date: "",
    phone_number: "",
    email_address: "",
    wedding_photo: "",
    isEmailAvailable: false,
    isPhoneValid: false,
  });

  const [previewUrl, setPreviewUrl] = useState("");
  const [registrants, setRegistrants] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchRegistrants = async () => {
      const endpoint = 'https://tsoministries.com/wp-json/wp/v2/pop_my_cherry';
      let page = 1;
      let allRegistrants = [];

      try {
        while (true) {
          const response = await fetch(`${endpoint}?per_page=100&page=${page}&_fields=id,email_address`);

          if (!response.ok) {
            if (response.status === 400) break; // No more pages
            throw new Error(`Error fetching page ${page}`);
          }

          const data = await response.json();
          if (data.length === 0) break; // Stop if no data

          allRegistrants = [...allRegistrants, ...data];
          page += 1;
        }

        setRegistrants(allRegistrants);
      } catch (error) {
        console.error('Failed to fetch registrants:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchRegistrants();
  }, []);
  const totalRegistered = registrants.length;
  // ✨ Calculate maxCapacity dynamically
  const maxCapacity = (() => {
    if (totalRegistered <= 100) return 200;
    if (totalRegistered <= 200) return 250;
    return Math.ceil(totalRegistered * 1.4); // 140% of totalRegistered
  })();

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setForm({ ...form, wedding_photo: reader.result });
        setPreviewUrl(URL.createObjectURL(file));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!form.isEmailAvailable) {
      Toast.error('Email is already in use. Please use another email.');
      return;
    }

    if (!form.isPhoneValid) {
      Toast.error('Your Phone number is not Valid');
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await fetch("https://tsoministries.com/wp-json/custom-form/v1/pop-my-cherry", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });

      const result = await response.json();
      if (response.ok) {
        Toast.success("Registration successful! We'll be in touch soon.");
        setForm({
          full_name: "",
          age: "",
          marriage_date: "",
          phone_number: "",
          email_address: "",
          wedding_photo: "",
          isEmailAvailable: false,
          isPhoneValid: false,
        });
        setPreviewUrl("");
      } else {
        throw new Error(result.message || 'Something went wrong');
      }
    } catch (error) {
      Toast.error(error.message || 'Failed to submit form');
      console.log(error.message);
    } finally {
      setIsSubmitting(false);
    }
  };


  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="min-h-screen bg-gradient-to-b from-gray-900 to-black text-white pt-0"
    >
      {/* Hero Section */}
      <div className="relative mb-16 overflow-hidden">
        <motion.div
          initial={{ scale: 1.2 }}
          animate={{ scale: 1 }}
          transition={{ duration: 1.5 }}
          className="absolute inset-0"
        >
          <div
            className="absolute inset-0 bg-cover bg-center"
            style={{
              backgroundImage: "url('https://images.unsplash.com/photo-1519741497674-611481863552')",
              filter: "blur(4px)"
            }}
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-black/80 backdrop-blur-sm" />
        </motion.div>

        <div className="relative container mx-auto mt-32 mb-14 px-4 h-full flex flex-col items-center justify-center text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
          >
            <h1 className="text-5xl md:text-6xl font-bold mb-4 font-martini">Pop My Cherry</h1>
            <p className="text-md md:text-xl px-3 md:px-0 text-gray-200 max-w-2xl mx-auto font-playfair_display">
              Vaginismus is the enemy; not your spouse!
            </p>
            <p className="text-md md:text-xl px-3 md:px-0 text-gray-200 max-w-2xl mx-auto mt-4 font-playfair_display">
              In this meeting, we will discuss all the tips regarding how to break through it.
              Have you gotten my book? Get it so we can talk better.
              Listen, it’s not a death sentence and it is totally treatable.
            </p>
            <p className="text-md md:text-xl px-3 md:px-0 text-gray-200 max-w-2xl mx-auto mt-4 font-playfair_display">
              CAVEAT: I only teach married people. 🙏🏾
            </p>
            <p className="text-md md:text-xl px-3 md:px-0 text-gray-200 max-w-2xl mx-auto font-playfair_display">
              ETSO.
            </p>
          </motion.div>
        </div>

        {/* Floating Elements */}
        {Array.from({ length: 10 }).map((_, i) => (
          <motion.div
            key={i}
            className="absolute"
            initial={{
              top: `${Math.random() * 100}%`,
              left: `${Math.random() * 100}%`,
              scale: 0,
              rotate: 0
            }}
            animate={{
              y: [-20, 20],
              scale: [1, 1.2, 1],
              rotate: 360
            }}
            transition={{
              duration: Math.random() * 3 + 2,
              repeat: Infinity,
              repeatType: "reverse"
            }}
          >
            <Heart className="w-4 h-4 text-pink-400/30" />
          </motion.div>
        ))}
      </div>

      {/* Form Section */}
      <div className="container mx-auto px-4 pb-24 max-w-7xl mx-auto">

        <div className='grid md:grid-cols-1 lg:grid-cols-2 gap-8'>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className=""
          >
            <form onSubmit={handleSubmit} className="bg-white/5 backdrop-blur-sm rounded-2xl p-8 border border-white/10">
              <div className="space-y-6">
                {/* Full Name */}
                <div>
                  <label className="block text-sm font-medium mb-2">Full Name</label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                    <input
                      type="text"
                      name="full_name"
                      value={form.full_name}
                      onChange={handleChange}
                      className="w-full pl-10 pr-4 py-3 bg-white/5 rounded-lg border border-white/10 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all duration-300"
                      required
                    />
                  </div>
                </div>

                {/* Age */}
                <div>
                  <label className="block text-sm font-medium mb-2">Age</label>
                  <div className="relative">
                    <input
                      type="number"
                      name="age"
                      value={form.age}
                      onChange={handleChange}
                      className="w-full px-4 py-3 bg-white/5 rounded-lg border border-white/10 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all duration-300"
                      required
                    />
                  </div>
                </div>

                {/* Marriage Date */}
                <div>
                  <label className="block text-sm font-medium mb-2">Marriage Date</label>
                  <div className="relative">
                    <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                    <input
                      type="date"
                      name="marriage_date"
                      value={form.marriage_date}
                      onChange={handleChange}
                      className="w-full pl-10 pr-4 py-3 bg-white/5 rounded-lg border border-white/10 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all duration-300"
                      required
                    />
                  </div>
                </div>

                {/* Phone Number */}
                <CherryPhoneInput
                  value={form.phone_number}
                  onChange={(e) => handleChange(e)}
                  setForm={setForm}
                />

                {/* Email Address */}
                <CherryEmailInput
                  value={form.email_address}
                  onChange={(e) => handleChange(e)}
                  setForm={setForm}
                />

                {/* Wedding Photo */}
                <div>
                  <label className="block text-sm font-medium mb-2">Wedding Photo</label>
                  <div className="relative">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleFileChange}
                      className="hidden"
                      id="wedding-photo"
                      required
                    />
                    <label
                      htmlFor="wedding-photo"
                      className="flex items-center justify-center w-full p-4 border-2 border-dashed border-white/20 rounded-lg hover:border-amber-500/50 transition-colors cursor-pointer group"
                    >
                      {previewUrl ? (
                        <div className="relative w-full aspect-video rounded-lg overflow-hidden">
                          <img
                            src={previewUrl}
                            alt="Wedding preview"
                            className="absolute inset-0 w-full h-full object-cover"
                          />
                          <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                            <p className="text-white">Change photo</p>
                          </div>
                        </div>
                      ) : (
                        <div className="text-center">
                          <Upload className="w-8 h-8 mx-auto mb-2 text-gray-400 group-hover:text-amber-500 transition-colors" />
                          <p className="text-gray-400 group-hover:text-white transition-colors">
                            Upload your wedding photo
                          </p>
                        </div>
                      )}
                    </label>
                  </div>
                </div>

                <motion.button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full py-3 bg-gradient-to-r from-amber-500 to-amber-600 text-white rounded-lg hover:from-amber-600 hover:to-amber-700 transition-all duration-300 flex items-center justify-center space-x-2"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <span>{isSubmitting ? 'Submitting...' : 'Register Now'}</span>
                </motion.button>
              </div>
            </form>
          </motion.div>

          <div className="space-y-8 h-full overflow-hidden rounded-2xl">
            <motion.div
              key={material.url}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0 * 0.1 }}
              className="group relative overflow-hidden rounded-xl bg-white/5 backdrop-blur-sm border border-white/10 flex flex-row h-[20vh] mb-5"
            >
              <div className="aspect-video relative overflow-hidden">
                <img
                  src={material.image}
                  alt={material.title}
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                />
                {/* <div className="absolute inset-0 bg-gradient-to-t from-gray-900 to-transparent" /> */}
                <div className="absolute top-4 right-4">
                  <span className="px-3 py-1 bg-white/20 text-white text-sm rounded-full backdrop-blur-sm">
                    {material.format}
                  </span>
                </div>
              </div>

              <div className="p-6">
                <h3 className="text-xl font-bold mb-2">{material.title}</h3>
                <p className="text-gray-400 mb-4">{material.description}</p>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-500">{material.size}</span>
                  <motion.button
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                    className="p-2 bg-amber-200/10 rounded-full hover:bg-amber-200/20 transition-colors"
                    onClick={() => window.open(material.url)}
                  >
                    <Download className="w-5 h-5 text-amber-400"/>
                  </motion.button>
                </div>
              </div>
            </motion.div>
            <RegistrationCounter
              totalRegistered={totalRegistered}
              maxCapacity={maxCapacity}
              eventDate="Will Be Released soon"
              eventLocation="Will Be Released soon"
            />
          </div>
        </div>
      </div>
    </motion.div>
  )
}



const CherryPhoneInput = ({ value, onChange, setForm }) => {
  const [phone, setPhone] = useState(value || "");
  const [isValid, setIsValid] = useState(true);

  useEffect(() => {
    if (!phone) {
      setIsValid(true);
      return;
    }

    const internationalPhoneRegex = /^\+[1-9]\d{7,14}$/;
    // + followed by 8 to 15 digits, first digit can't be 0

    setIsValid(internationalPhoneRegex.test(phone));
  }, [phone]);

  useEffect(() => {
    if (phone.length >= 7 && isValid === true) {
      setForm((prev) => ({ ...prev, isPhoneValid: true }));
    } else {
      setForm((prev) => ({ ...prev, isPhoneValid: false }));
    }
  }, [phone, isValid, setForm]);

  const handleChange = (e) => {
    setPhone(e.target.value);
    onChange({ target: { name: "phone_number", value: e.target.value } });
  };

  return (
    <div className="mb-6">
      <label className="block text-sm font-medium mb-2">Phone Number</label>
      <div className="relative">
        <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
        <input
          type="tel"
          name="phone_number"
          value={phone}
          onChange={handleChange}
          className="w-full pl-10 pr-4 py-3 bg-white/5 rounded-lg border border-white/10 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all duration-300"
          required
        />
        {isValid !== null && phone.length > 0 && (
          <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
            {isValid ? (
              <CheckCircle className="text-green-500 w-5 h-5" />
            ) : (
              <XCircle className="text-red-500 w-5 h-5" />
            )}
          </div>
        )}
      </div>

      {!isValid && phone.length > 0 && (
        <p className="text-red-500 text-sm mt-1">Invalid phone number format.</p>
      )}
    </div>
  );
};





// ---------------------------
// CherryEmailInput component
// ---------------------------
const CherryEmailInput = ({ value, onChange, setForm }) => {
  const [email, setEmail] = useState(value || "");
  const [isValid, setIsValid] = useState(true);
  const [isAvailable, setIsAvailable] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!email) {
      setIsValid(true);
      setIsAvailable(null);
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    setIsValid(emailRegex.test(email));

    if (!emailRegex.test(email)) {
      setIsAvailable(null);
      return;
    }

    const delay = setTimeout(async () => {
      setLoading(true);
      const available = await checkEmailAvailability(email);
      setIsAvailable(available);
      setLoading(false);
    }, 500);

    return () => clearTimeout(delay);
  }, [email]);

  useEffect(() => {
    if (email.length >= 4 && isAvailable === true && isValid === true) {
      setForm((prev) => ({ ...prev, isEmailAvailable: true }));
    } else {
      setForm((prev) => ({ ...prev, isEmailAvailable: false }));
    }
  }, [email, isAvailable, isValid, setForm]);

  const handleChange = (e) => {
    setEmail(e.target.value);
    onChange({ target: { name: "email_address", value: e.target.value } });
  };

  return (
    <div className="mb-6">
      <label className="block text-sm font-medium mb-2">Email Address</label>
      <div className="relative">
        <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
        <input
          type="email"
          name="email_address"
          value={email}
          onChange={handleChange}
          className="w-full pl-10 pr-4 py-3 bg-white/5 rounded-lg border border-white/10 focus:outline-none focus:ring-2 focus:ring-amber-600 focus:border-transparent transition-all duration-300"
          required
        />
        {loading ? (
          <div className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400">
            Checking...
          </div>
        ) : isValid && isAvailable !== null && (
          <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
            {isAvailable ? (
              <CheckCircle className="text-green-500 w-5 h-5" />
            ) : (
              <XCircle className="text-red-500 w-5 h-5" />
            )}
          </div>
        )}
      </div>

      {!isValid && email.length > 0 && (
        <p className="text-red-500 text-sm mt-1">Invalid email format.</p>
      )}

      {isValid && isAvailable === false && (
        <p className="text-red-500 text-sm mt-1">
          This email is already in use. You are registered already.
        </p>
      )}
    </div>
  );
};

// -----------------------------------------
// checkEmailAvailability (utility function)
// -----------------------------------------
async function checkEmailAvailability(email) {
  const endpoint = 'https://tsoministries.com/wp-json/wp/v2/pop_my_cherry';
  let page = 1;
  let emailExists = false;

  try {
    while (true) {
      const response = await fetch(`${endpoint}?per_page=100&page=${page}&_fields=id,email_address`);

      if (!response.ok) {
        throw new Error(`Error fetching page ${page}`);
      }

      const data = await response.json();

      // Check if email matches in current page
      for (const item of data) {
        if (item.email_address && item.email_address.toLowerCase() === email.toLowerCase()) {
          emailExists = true;
          break;
        }
      }

      // If found, no need to fetch next page
      if (emailExists) break;

      // If less than 100 items, it's the last page
      if (data.length < 100) break;

      page++;
    }

    return !emailExists; // true = available, false = already used
  } catch (error) {
    console.error('Failed to check email:', error);
    return false; // Assume unavailable if something failed
  }
}